import { useMemo, useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/Checkbox'
import { useAppDispatch } from '@/redux/hooks'
import {
  addGroupMembers,
  createGroupChat,
} from '@/redux/asyncThunks/negotiationWebsocket'
import { selectRoomPlayers } from '@/redux/selectors/roomSelector'
import { useSelector } from 'react-redux'
import { useEffectiveUser } from '@/hooks/useEffectiveUser'
import { toast } from 'sonner'

type GroupChatModalProps = {
  isOpen: boolean
  onClose: () => void
  mode: 'create' | 'add'
  negotiationId?: string
  /** Emails already in the chat (add mode) - shown disabled. */
  existingEmails?: string[]
  /** Emails with an unanswered invitation (add mode) - shown disabled. */
  invitedEmails?: string[]
  phaseId?: string
}

const GroupChatModal: React.FC<GroupChatModalProps> = ({
  isOpen,
  onClose,
  mode,
  negotiationId,
  existingEmails = [],
  invitedEmails = [],
  phaseId,
}) => {
  const dispatch = useAppDispatch()
  const roomPlayers = useSelector(selectRoomPlayers)
  const { user } = useEffectiveUser()
  const [selected, setSelected] = useState<string[]>([])
  const [isSubmitting, setIsSubmitting] = useState(false)

  const candidates = useMemo(
    () =>
      roomPlayers.filter((player) => player.member_email !== user?.email),
    [roomPlayers, user?.email]
  )

  const toggle = (email: string) => {
    setSelected((prev) =>
      prev.includes(email)
        ? prev.filter((e) => e !== email)
        : [...prev, email]
    )
  }

  const handleSubmit = async () => {
    if (selected.length === 0) return
    setIsSubmitting(true)
    try {
      if (mode === 'create') {
        await dispatch(
          createGroupChat({ inviteeEmails: selected, phaseId })
        ).unwrap()
        toast.success('Group chat created - invitations sent')
      } else if (negotiationId) {
        await dispatch(
          addGroupMembers({ negotiationId, inviteeEmails: selected })
        ).unwrap()
        toast.success('Invitations sent')
      }
      setSelected([])
      onClose()
    } catch (err) {
      toast.error(
        typeof err === 'string' ? err : 'Could not send group invitations'
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setSelected([])
      onClose()
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className='sm:max-w-md'>
        <DialogHeader>
          <DialogTitle>
            {mode === 'create' ? 'Start a group chat' : 'Add members'}
          </DialogTitle>
          <DialogDescription>
            {mode === 'create'
              ? 'Pick one or more players to invite. The chat begins as soon as the first player joins.'
              : 'New members only see messages sent after they join.'}
          </DialogDescription>
        </DialogHeader>

        <div className='max-h-64 space-y-1 overflow-y-auto py-1'>
          {candidates.length === 0 ? (
            <p className='py-4 text-center text-sm text-gray-500'>
              No other players in this room
            </p>
          ) : (
            candidates.map((player) => {
              const inChat = existingEmails.includes(player.member_email)
              const invited = invitedEmails.includes(player.member_email)
              const alreadyIn = inChat || invited
              const displayName =
                player.member_name?.trim() || player.member_email
              return (
                <label
                  key={player.member_email}
                  className={
                    alreadyIn
                      ? 'flex cursor-not-allowed items-center gap-2 rounded-md px-2 py-1.5 text-sm opacity-50'
                      : 'flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-slate-100'
                  }
                >
                  <Checkbox
                    checked={
                      alreadyIn || selected.includes(player.member_email)
                    }
                    disabled={alreadyIn}
                    onChange={() => toggle(player.member_email)}
                  />
                  <span className='min-w-0 flex-1 truncate'>
                    {displayName}
                  </span>
                  {alreadyIn && (
                    <span className='text-xs text-gray-400'>
                      {inChat ? 'In chat' : 'Invited'}
                    </span>
                  )}
                </label>
              )
            })
          )}
        </div>

        <DialogFooter>
          <Button variant='outline' onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button
            className='bg-dark-blue text-white'
            disabled={selected.length === 0 || isSubmitting}
            onClick={handleSubmit}
          >
            {isSubmitting
              ? 'Sending...'
              : mode === 'create'
                ? `Invite ${selected.length || ''}`.trim()
                : 'Add members'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default GroupChatModal
